import React, { useCallback, useEffect, useState } from 'react'
import {
  Grid,
  GridColumn,
  GridDataStateChangeEvent,
  GridRowClickEvent,
  GridToolbar,
} from '@progress/kendo-react-grid'
import { process, State } from '@progress/kendo-data-query'
import { Dialog, DialogActionsBar } from '@progress/kendo-react-dialogs'
import { TabStrip, TabStripTab } from '@progress/kendo-react-layout'
import { Button } from '@progress/kendo-react-buttons'
import { Form, Field, FormElement } from '@progress/kendo-react-form'
import { FaEdit, FaFileContract, FaFilePdf, FaSyncAlt, FaTrash } from 'react-icons/fa'
import dayjs from 'dayjs'

import {
  FormComboBox,
  FormDatePicker,
  FormDropDownList,
  FormDropDownListFilter,
  FormInput,
  FormMultiSelect,
  FormNumericTextBox,
  FormTextArea,
} from '../FormComponents'
import TabSectionHeader from './TabSectionHeader'
import { useToast } from '../../../contexts/ToastContext'
import {
  dropDown,
  ScSupplyOption,
  SubcontractAgreement,
} from '../../../data/interfaces'
import {
  createSubcontractAgreement,
  deleteSubcontractAgreement,
  getScSupplyOptions,
  getSubcontractAgreementPdf,
  getSubcontractAgreementsByJob,
  updateSubcontractAgreement,
} from '../../../data/subcontractAgreementData'
import { getXeroSuppliersDropdown } from '../../../data/xeroData'
import { getEmployeeOptions } from '../../../data/employeeData'

interface SubContractsProps {
  jobId: number
  lockEdit?: boolean
}

// SQL Server stores these as datetimes but the user only ever picks a day, so
// send a local timestamp rather than toISOString() - the latter shifts the date
// back a day for anything picked during BST.
const toLocalDateString = (date: Date | null | undefined) =>
  date ? dayjs(date).format('YYYY-MM-DDTHH:mm:ss') : null

// Application and certification dates are a day of the month, typed rather than
// stepped through with spinners. The column carries a CHECK constraint for
// 1-31, so anything outside that has to be caught here rather than rejected by
// the database after the user has filled the rest of the form in.
//
// Empty is allowed: both fields are optional.
const dayOfMonthValidator = (value: any) => {
  if (value === null || value === undefined || String(value).trim() === '') {
    return ''
  }

  const day = Number(String(value).trim())

  return Number.isInteger(day) && day >= 1 && day <= 31
    ? ''
    : 'Enter a day of the month between 1 and 31.'
}

const formatMoney = (value: number | null | undefined) =>
  typeof value === 'number'
    ? `£${value.toLocaleString('en-GB', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })}`
    : ''

const SubContracts: React.FC<SubContractsProps> = ({ jobId, lockEdit = false }) => {
  const { showToast } = useToast()

  const [agreements, setAgreements] = useState<SubcontractAgreement[]>([])
  const [supplyOptions, setSupplyOptions] = useState<ScSupplyOption[]>([])
  const [supplierData, setSupplierData] = useState<dropDown[]>([])
  const [employeeData, setEmployeeData] = useState<dropDown[]>([])
  // The register is a variable length list, which a Kendo Form field cannot
  // hold, so it lives beside the form and is merged in on submit.
  const [documents, setDocuments] = useState<string[]>([])
  const [selectedTab, setSelectedTab] = useState(0)

  const [dataState, setDataState] = useState<State>({ take: 10, skip: 0 })
  const [editDialogVisible, setEditDialogVisible] = useState(false)
  const [deleteDialogVisible, setDeleteDialogVisible] = useState(false)
  const [currentAgreement, setCurrentAgreement] = useState<SubcontractAgreement | null>(null)
  const [isNewAgreement, setIsNewAgreement] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const [isLoading, setIsLoading] = useState(false)

  const supplyOptionsData: dropDown[] = supplyOptions.map((o) => ({
    text: o.name,
    value: o.scSupplyOptionId,
  }))

  const fetchAgreements = useCallback(async () => {
    try {
      setAgreements(await getSubcontractAgreementsByJob(jobId))
    } catch (error) {
      console.error('Error fetching sub-contract agreements:', error)
      showToast('Failed to load sub-contract agreements', 'error')
    }
    // showToast is stable enough here; refetching is driven by the job only
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [jobId])

  useEffect(() => {
    if (jobId > 0) {
      fetchAgreements()
    }
  }, [jobId, fetchAgreements])

  useEffect(() => {
    const fetchOptions = async () => {
      try {
        const [supply, suppliers] = await Promise.all([
          getScSupplyOptions(),
          getXeroSuppliersDropdown(),
        ])
        setSupplyOptions(supply)
        setSupplierData(suppliers)
      } catch (error) {
        console.error('Error fetching sub-contract options:', error)
        showToast('Failed to load sub-contract options', 'error')
      }
    }

    fetchOptions()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const processedData = process(agreements, dataState)

  const handleAddAgreement = () => {
    if (lockEdit) {
      showToast('Cannot add sub-contract agreements while edit is locked', 'error')
      return
    }
    setCurrentAgreement(null)
    setIsNewAgreement(true)
    setDocuments([])
    setSelectedTab(0)
    setFormKey((prev) => prev + 1)
    setEditDialogVisible(true)
  }

  const handleEditAgreement = (agreement: SubcontractAgreement) => {
    if (lockEdit) {
      showToast('Cannot edit sub-contract agreements while edit is locked', 'error')
      return
    }
    setCurrentAgreement(agreement)
    setIsNewAgreement(false)
    setDocuments([...(agreement.documents || [])])
    setSelectedTab(0)
    setFormKey((prev) => prev + 1)
    setEditDialogVisible(true)
  }

  const handleDeleteAgreement = (agreement: SubcontractAgreement) => {
    if (lockEdit) {
      showToast('Cannot delete sub-contract agreements while edit is locked', 'error')
      return
    }
    setCurrentAgreement(agreement)
    setDeleteDialogVisible(true)
  }

  useEffect(() => {
    // Everyone active - an empty search term is "no filter" to the helper.
    getEmployeeOptions()
      .then(setEmployeeData)
      // The agreement still saves without these, so no toast.
      .catch((error) => console.error('Error loading employees:', error))
  }, [])

  const handleFormSubmit = async (values: { [name: string]: any }) => {
    const payload = {
      xeroSupplierId: values.supplier?.value ? Number(values.supplier.value) : null,
      supplyOptionIds: ((values.supplyOptions || []) as dropDown[]).map((o) =>
        typeof o.value === 'number' ? o.value : parseInt(String(o.value), 10)
      ),
      quotationDate: toLocalDateString(values.quotationDate),
      works: values.works || null,
      orderLet: values.orderLet || null,
      totalValue:
        values.totalValue === null || values.totalValue === undefined || values.totalValue === ''
          ? null
          : Number(values.totalValue),
      applicationDate: values.applicationDate ? Number(values.applicationDate) : null,
      certificationDate: values.certificationDate ? Number(values.certificationDate) : null,
      paymentTerms: values.paymentTerms?.trim() ? values.paymentTerms.trim() : null,
      retention: values.retention || null,
      supplierAddress: values.supplierAddress?.trim() || null,
      // Free text either way: the three options are a starting point, not a
      // closed list, so whatever is in the box is what gets stored.
      contractBasis:
        (typeof values.contractBasis === 'string'
          ? values.contractBasis
          : values.contractBasis?.text || ''
        ).trim() || null,
      preOrderMeetingMinutesDated: values.preOrderMeetingMinutesDated?.trim() || null,
      programme: values.programme?.trim() || null,
      contract: values.contract?.trim() || null,
      headOfConstructionEmployeeId: values.headOfConstruction?.value || null,
      projectCoordinatorEmployeeId: values.projectCoordinator?.value || null,
      projectManagerEmployeeId: values.projectManager?.value || null,
      quantitySurveyorEmployeeId: values.quantitySurveyor?.value || null,
      documents: documents.map((d) => d.trim()).filter((d) => d.length > 0),
    }

    setIsLoading(true)
    try {
      if (isNewAgreement) {
        const created = await createSubcontractAgreement({ jobId, ...payload })
        showToast(`Sub-contract agreement ${created.reference} created`, 'success')
      } else if (currentAgreement) {
        await updateSubcontractAgreement({
          subcontractAgreementId: currentAgreement.subcontractAgreementId,
          ...payload,
        })
        showToast(`Sub-contract agreement ${currentAgreement.reference} updated`, 'success')
      }

      await fetchAgreements()
      setEditDialogVisible(false)
      setCurrentAgreement(null)
    } catch (error) {
      console.error('Error saving sub-contract agreement:', error)
      showToast('Failed to save sub-contract agreement', 'error')
    } finally {
      setIsLoading(false)
    }
  }

  const handleConfirmDelete = async () => {
    if (currentAgreement) {
      setIsLoading(true)
      try {
        await deleteSubcontractAgreement(currentAgreement.subcontractAgreementId)
        await fetchAgreements()
        showToast('Sub-contract agreement deleted', 'success')
      } catch (error) {
        console.error('Error deleting sub-contract agreement:', error)
        showToast('Failed to delete sub-contract agreement', 'error')
      } finally {
        setIsLoading(false)
      }
    }
    setDeleteDialogVisible(false)
    setCurrentAgreement(null)
  }

  const handleRowClick = (event: GridRowClickEvent) => {
    handleEditAgreement(event.dataItem)
  }

  const totalValueCell = (props: any) => <td>{formatMoney(props.dataItem.totalValue)}</td>

  // The PDF is generated by Power Automate after the save returns, so it may
  // not be there the instant an agreement is created - the grid's Refresh
  // button re-checks without leaving the tab.
  const handleViewPdf = async (agreement: SubcontractAgreement) => {
    if (!agreement.hasPdf) {
      showToast('The PDF for this agreement has not been generated yet', 'info')
      return
    }

    // Opened before the await so the browser still attributes the tab to the
    // user's click; one opened after an async gap is treated as a popup and
    // blocked.
    const tab = window.open('', '_blank')

    try {
      const blob = await getSubcontractAgreementPdf(agreement.subcontractAgreementId)
      const url = URL.createObjectURL(blob)

      if (tab) {
        tab.location.href = url
      } else {
        window.open(url, '_blank')
      }

      // The new tab reads the document through this URL, so it can only be
      // released once that has had a chance to happen.
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    } catch (error) {
      tab?.close()
      console.error('Error loading sub-contract agreement PDF:', error)
      showToast('Failed to load the sub-contract agreement PDF', 'error')
    }
  }

  const actionCell = (props: any) => (
    <td style={{ textAlign: 'center' }}>
      <div style={{ display: 'flex', justifyContent: 'center', gap: '10px' }}>
        {/* Viewing is not editing, so this stays available in read only mode.
            The click is stopped from bubbling or the row click behind it would
            open the edit dialog at the same time. */}
        <FaFilePdf
          style={{
            cursor: props.dataItem.hasPdf ? 'pointer' : 'not-allowed',
            color: props.dataItem.hasPdf ? '#d32f2f' : '#b8bfc9',
            fontSize: '16px',
          }}
          title={
            props.dataItem.hasPdf
              ? 'View PDF'
              : 'PDF not generated yet - use Refresh in a moment'
          }
          onClick={(event) => {
            event.stopPropagation()
            handleViewPdf(props.dataItem)
          }}
        />
        {!lockEdit && (
          <>
            <FaEdit
              style={{ cursor: 'pointer', color: '#1e2a4e', fontSize: '16px' }}
              title='Edit'
              onClick={() => handleEditAgreement(props.dataItem)}
            />
            <FaTrash
              style={{ cursor: 'pointer', color: '#e9530d', fontSize: '16px' }}
              title='Delete'
              onClick={() => handleDeleteAgreement(props.dataItem)}
            />
          </>
        )}
      </div>
    </td>
  )

  const totalAgreementValue = agreements.reduce((sum, a) => sum + (a.totalValue || 0), 0)

  // Prefers the live list so the dropdown matches by dataItemKey, falling back
  // to the name on the agreement if that person has since left.
  const employeeOption = (id?: number | null, name?: string | null) =>
    id ? employeeData.find((e) => e.value === id) || { text: name || '', value: id } : null

  // A starting point rather than a closed list - the box accepts anything typed
  // over them.
  const contractBasisOptions = ['Fixed lump sum', 'Remeasurable', 'Day works']

  const initialValues = {
    // Prefer the live dropdown entry so the drop down matches by dataItemKey,
    // falling back to the name stored against the agreement if that supplier
    // has since been archived out of the list.
    supplier: currentAgreement?.xeroSupplierId
      ? supplierData.find((s) => s.value === currentAgreement.xeroSupplierId) || {
          text: currentAgreement.supplierName || '',
          value: currentAgreement.xeroSupplierId,
        }
      : null,
    supplyOptions: (currentAgreement?.supplyOptions || []).map((o) => ({
      text: o.name,
      value: o.scSupplyOptionId,
    })),
    quotationDate: currentAgreement?.quotationDate
      ? new Date(currentAgreement.quotationDate)
      : null,
    works: currentAgreement?.works || '',
    orderLet: currentAgreement?.orderLet || '',
    totalValue: currentAgreement?.totalValue ?? null,
    applicationDate: currentAgreement?.applicationDate ?? '',
    certificationDate: currentAgreement?.certificationDate ?? '',
    // Free text now. Agreements saved before that change fall back to the name
    // of the option they were given, so an existing record still reads the same
    // and can be edited as text from here on.
    paymentTerms:
      currentAgreement?.paymentTerms || currentAgreement?.paymentOptionName || '',
    retention: currentAgreement?.retention || '',
    supplierAddress: currentAgreement?.supplierAddress || '',
    contractBasis: currentAgreement?.contractBasis || '',
    preOrderMeetingMinutesDated: currentAgreement?.preOrderMeetingMinutesDated || '',
    programme: currentAgreement?.programme || '',
    contract: currentAgreement?.contract || '',
    // An empty entry is allowed, so these start null rather than at a default
    // person.
    headOfConstruction: employeeOption(
      currentAgreement?.headOfConstructionEmployeeId,
      currentAgreement?.headOfConstructionName
    ),
    projectCoordinator: employeeOption(
      currentAgreement?.projectCoordinatorEmployeeId,
      currentAgreement?.projectCoordinatorName
    ),
    projectManager: employeeOption(
      currentAgreement?.projectManagerEmployeeId,
      currentAgreement?.projectManagerName
    ),
    quantitySurveyor: employeeOption(
      currentAgreement?.quantitySurveyorEmployeeId,
      currentAgreement?.quantitySurveyorName
    ),
  }

  return (
    <div>
      <TabSectionHeader
        icon={<FaFileContract />}
        title='Sub-Contracts'
        description='Sub-contract agreements raised against this job'
        actions={
          <button
            type='button'
            title='Add new sub-contract agreement'
            onClick={handleAddAgreement}
            disabled={lockEdit}
            className='jobsection__action-btn'
          >
            New Sub-Contract Agreement
          </button>
        }
      />
      <div style={{ padding: '0 1rem 1rem' }}>
        <Grid
          data={processedData}
          {...dataState}
          onDataStateChange={(event: GridDataStateChangeEvent) => setDataState(event.dataState)}
          onRowClick={handleRowClick}
          style={{ height: '400px' }}
        >
          <GridToolbar>
            <span style={{ color: '#666' }}>
              Total Agreements: {agreements.length} | Total Value: {formatMoney(totalAgreementValue)}
            </span>
            <button
              type='button'
              title='Re-check for newly generated PDFs'
              onClick={fetchAgreements}
              className='jobsection__action-btn'
              style={{ marginLeft: 'auto', display: 'flex', alignItems: 'center', gap: '0.4rem' }}
            >
              <FaSyncAlt />
              Refresh
            </button>
          </GridToolbar>
          <GridColumn field='reference' title='Agreement Id' width='180px' />
          <GridColumn field='supplierName' title='Supplier' width='280px' />
          <GridColumn field='totalValue' title='Total Value' width='150px' cell={totalValueCell} />
          <GridColumn title='Actions' width='150px' cell={actionCell} />
        </Grid>
      </div>

      {editDialogVisible && (
        <Dialog
          title={
            isNewAgreement
              ? 'New Sub-Contract Agreement'
              : `Edit Sub-Contract Agreement ${currentAgreement?.reference || ''}`
          }
          onClose={() => setEditDialogVisible(false)}
          appendTo={document.body}
          width='650px'
        >
          <Form
            key={formKey}
            initialValues={initialValues}
            onSubmit={handleFormSubmit}
            render={(formRenderProps) => (
              <FormElement style={{ maxWidth: 650, margin: '0' }}>
                <div className='form__details__subcontainer'>
                  <div style={{ marginBottom: '0.75rem' }}>
                    <div style={{ fontSize: '10pt', color: 'rgb(36,32,56)' }}>
                      Sub Contract Agreement Id:
                    </div>
                    <div style={{ fontWeight: 600 }}>
                      {isNewAgreement
                        ? 'Generated automatically when saved'
                        : currentAgreement?.reference}
                    </div>
                  </div>

                  <TabStrip
                    selected={selectedTab}
                    onSelect={(e) => setSelectedTab(e.selected)}
                    // Every tab stays mounted: a Kendo Field that unmounts is
                    // no longer registered with the form, so a value typed on
                    // one tab could be lost by saving from another.
                    keepTabsMounted={true}
                  >
                    <TabStripTab title='Supplier'>
                  <Field
                    name='supplier'
                    component={FormDropDownListFilter}
                    label='Supplier:'
                    data={supplierData}
                    textField='text'
                    dataItemKey='value'
                    defaultText='Select a supplier'
                    defaultValue={0}
                  />
                  <Field
                    name='supplierAddress'
                    component={FormTextArea}
                    label='Supplier Address:'
                    rows={3}
                  />
                  <Field
                    name='supplyOptions'
                    component={FormMultiSelect}
                    label='Supply options:'
                    data={supplyOptionsData}
                  />
                    </TabStripTab>

                    <TabStripTab title='Agreement'>
                  <Field
                    name='quotationDate'
                    component={FormDatePicker}
                    label='Quotation Date:'
                  />
                  <Field name='works' component={FormTextArea} label='Works:' rows={3} />
                  <Field name='orderLet' component={FormInput} label='Order Let:' />
                  <Field
                    name='totalValue'
                    component={FormNumericTextBox}
                    label='Total Value (£):'
                    format='n2'
                    step={0.01}
                  />
                    </TabStripTab>

                    <TabStripTab title='Payment'>
                  <Field
                    name='applicationDate'
                    component={FormInput}
                    label='Application Date (day of month, 1-31):'
                    validator={dayOfMonthValidator}
                  />
                  <Field
                    name='certificationDate'
                    component={FormInput}
                    label='Certification Date (day of month, 1-31):'
                    validator={dayOfMonthValidator}
                  />
                  <Field
                    name='paymentTerms'
                    component={FormInput}
                    label='Payment Date:'
                    placeholder='e.g. 30 days from invoice'
                  />
                  <Field name='retention' component={FormInput} label='Retention:' />
                    </TabStripTab>

                    <TabStripTab title='Contract'>
                  <Field
                    name='contractBasis'
                    component={FormComboBox}
                    label='Contract Basis:'
                    data={contractBasisOptions}
                    allowCustom={true}
                    placeholder='Choose one, or type your own'
                  />
                  <Field
                    name='preOrderMeetingMinutesDated'
                    component={FormInput}
                    label='Pre Order Site Meeting Minutes Dated:'
                  />
                  <Field name='programme' component={FormInput} label='Programme:' />
                  <Field name='contract' component={FormInput} label='Contract:' />
                  <div style={{ marginTop: '1rem' }}>
                    <div style={{ fontSize: '10pt', color: 'rgb(36,32,56)', marginBottom: '0.4rem' }}>
                      Document Register:
                    </div>
                    {documents.map((doc, index) => (
                      <div
                        key={index}
                        style={{ display: 'flex', gap: '0.5rem', marginBottom: '0.4rem' }}
                      >
                        <input
                          className='k-input k-textbox k-input-md k-input-solid k-rounded-md'
                          value={doc}
                          maxLength={255}
                          placeholder='Document name'
                          style={{ flex: 1, padding: '0.25rem 0.5rem' }}
                          onChange={(e) => {
                            const next = [...documents]
                            next[index] = e.target.value
                            setDocuments(next)
                          }}
                        />
                        <Button
                          type='button'
                          fillMode='outline'
                          title='Remove'
                          onClick={() =>
                            setDocuments(documents.filter((_, i) => i !== index))
                          }
                        >
                          <FaTrash />
                        </Button>
                      </div>
                    ))}
                    <Button
                      type='button'
                      fillMode='outline'
                      onClick={() => setDocuments([...documents, ''])}
                    >
                      Add document
                    </Button>
                    {documents.length === 0 && (
                      <div style={{ fontSize: '9pt', color: '#5a6472', marginTop: '0.3rem' }}>
                        No documents listed. Blank lines are dropped when saved.
                      </div>
                    )}
                  </div>
                    </TabStripTab>

                    <TabStripTab title='Team'>
                  <Field
                    name='headOfConstruction'
                    component={FormDropDownList}
                    label='Head of Construction Contact:'
                    data={employeeData}
                    textField='text'
                    dataItemKey='value'
                    defaultText='None'
                  />
                  <Field
                    name='projectCoordinator'
                    component={FormDropDownList}
                    label='Project Coordinator:'
                    data={employeeData}
                    textField='text'
                    dataItemKey='value'
                    defaultText='None'
                  />
                  <Field
                    name='projectManager'
                    component={FormDropDownList}
                    label='Project Manager:'
                    data={employeeData}
                    textField='text'
                    dataItemKey='value'
                    defaultText='None'
                  />
                  <Field
                    name='quantitySurveyor'
                    component={FormDropDownList}
                    label='Quantity Surveyor:'
                    data={employeeData}
                    textField='text'
                    dataItemKey='value'
                    defaultText='None'
                  />
                    </TabStripTab>
                  </TabStrip>
                </div>
                <DialogActionsBar>
                  <Button type='button' onClick={() => setEditDialogVisible(false)}>
                    Cancel
                  </Button>
                  <Button
                    type='submit'
                    themeColor='primary'
                    disabled={!formRenderProps.allowSubmit || isLoading}
                  >
                    {isLoading ? 'Saving...' : isNewAgreement ? 'Create' : 'Update'}
                  </Button>
                </DialogActionsBar>
              </FormElement>
            )}
          />
        </Dialog>
      )}

      {deleteDialogVisible && (
        <Dialog
          title='Confirm Delete'
          onClose={() => setDeleteDialogVisible(false)}
          appendTo={document.body}
          width='400px'
        >
          <div style={{ padding: '1rem' }}>
            <p>Are you sure you want to delete this sub-contract agreement?</p>
            <p>
              <strong>Agreement Id:</strong> {currentAgreement?.reference}
            </p>
            <p>
              <strong>Supplier:</strong> {currentAgreement?.supplierName || '—'}
            </p>
            <p>
              <strong>Total Value:</strong> {formatMoney(currentAgreement?.totalValue)}
            </p>
          </div>
          <DialogActionsBar>
            <Button type='button' onClick={() => setDeleteDialogVisible(false)}>
              Cancel
            </Button>
            <Button
              type='button'
              themeColor='error'
              onClick={handleConfirmDelete}
              disabled={isLoading}
            >
              {isLoading ? 'Deleting...' : 'Delete'}
            </Button>
          </DialogActionsBar>
        </Dialog>
      )}
    </div>
  )
}

export default SubContracts
